import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package } from 'lucide-react';

const CategoryCard = ({ category, onClick }) => (
  <button
    onClick={onClick}
    className="group relative w-full rounded-2xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300"
    style={{ aspectRatio: '1594/656' }}
  >
    {category.image ? (
      <img
        src={category.image}
        srcSet={category.image_srcset || undefined}
        sizes="(min-width: 768px) 50vw, 100vw"
        alt={category.name}
        className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
        loading="lazy"
        decoding="async"
      />
    ) : (
      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
        <Package className="w-10 h-10 text-gray-300" />
      </div>
    )}
  </button>
);

const CategoryShowcase = ({ categories, newArrivalPhoto }) => {
  const navigate = useNavigate();

  const handleNewArrivalClick = useCallback(() => {
    const link = newArrivalPhoto?.link?.trim();
    if (!link) return;
    if (/^https?:\/\//i.test(link)) {
      window.location.href = link;
    } else {
      navigate(link.startsWith('/') ? link : `/${link}`);
    }
  }, [newArrivalPhoto, navigate]);

  if (!categories || categories.length === 0) return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-14">
      <div className="flex items-center justify-between mb-5 sm:mb-7">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Shop by Category</h2>
        <button
          onClick={() => navigate('/products')}
          className="text-sm font-medium text-primary-600 hover:text-primary-700 whitespace-nowrap flex-shrink-0"
        >
          View All Products
        </button>
      </div>

      {newArrivalPhoto?.image && (
        <button
          onClick={handleNewArrivalClick}
          className="group relative w-full md:w-[calc((100%-1.25rem)/2)] md:mx-auto block rounded-2xl overflow-hidden bg-gray-100 shadow-sm hover:shadow-xl transition-shadow duration-300 mb-5 sm:mb-7"
          style={{ aspectRatio: '1594/656' }}
        >
          <img
            src={newArrivalPhoto.image}
            srcSet={newArrivalPhoto.image_srcset || undefined}
            sizes="(min-width: 768px) 50vw, 100vw"
            alt={newArrivalPhoto.name || 'New Arrival'}
            className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            loading="lazy"
            decoding="async"
          />
        </button>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
        {categories.map(cat => (
          <CategoryCard key={cat.id} category={cat} onClick={() => navigate(`/category/${cat.id}`)} />
        ))}
      </div>
    </div>
  );
};

export default CategoryShowcase;
